import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useLang } from "../contexts/LanguageContext";
import { Upload, LogOut, User as UserIcon, Sparkles } from "lucide-react";
import Logo from "./Logo";
import VerifiedDot from "./VerifiedDot";
import NotificationBell from "./NotificationBell";
import NavMenu from "./NavMenu";
import { portfolioHref } from "../lib/routes";

export default function Navbar() {
  const { user, logout } = useAuth();
  const { lang, toggle, t } = useLang();
  const navigate = useNavigate();

  const onLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <header
      className="sticky top-0 z-40 border-b border-zinc-800/80 bg-[#36404A]/70 [backdrop-filter:blur(10px)]"
      data-testid="app-navbar"
    >
      <div className="mx-auto max-w-[1600px] px-4 sm:px-10 h-16 flex items-center justify-between gap-3">
        <Link
          to="/"
          className="flex items-center gap-3 sm:gap-4 group shrink-0"
          data-testid="navbar-brand-link"
        >
          <Logo size={36} className="text-sienna-400 shrink-0" />
          <div className="flex flex-col leading-none">
            <span className="hidden sm:inline font-serif text-2xl lg:text-[1.85rem] whitespace-nowrap tracking-tight text-zinc-50 font-normal">
              {t("brand")}
            </span>
            <span className="hidden lg:flex items-center gap-2 mt-1 whitespace-nowrap font-mono text-[9px] uppercase tracking-[0.32em] text-zinc-400">
              <span className="h-px w-4 bg-zinc-600" aria-hidden />
              Est. 2026
              <span className="h-px w-4 bg-zinc-600" aria-hidden />
            </span>
          </div>
        </Link>

        <nav className="flex items-center gap-1 lg:gap-2 shrink-0">
          <NavMenu user={user || null} />
          <NavLink
            to="/"
            className={({ isActive }) =>
              `hidden lg:inline-block whitespace-nowrap px-2 xl:px-4 py-2 text-sm font-light transition-colors ${
                isActive ? "text-zinc-50" : "text-zinc-500 hover:text-zinc-200"
              }`
            }
            data-testid="nav-feed-link"
          >
            {t("global_feed")}
          </NavLink>

          <NavLink
            to="/c"
            className={({ isActive }) =>
              `hidden lg:inline-block whitespace-nowrap px-2 xl:px-4 py-2 text-sm font-light transition-colors ${
                isActive ? "text-zinc-50" : "text-zinc-500 hover:text-zinc-200"
              }`
            }
            data-testid="nav-hubs-link"
          >
            {t("hubs")}
          </NavLink>

          <NavLink
            to="/categories"
            className={({ isActive }) =>
              `hidden lg:inline-block whitespace-nowrap px-2 xl:px-4 py-2 text-sm font-light transition-colors ${
                isActive ? "text-zinc-50" : "text-zinc-500 hover:text-zinc-200"
              }`
            }
            data-testid="nav-categories-link"
          >
            {t("genres")}
          </NavLink>

          <button
            onClick={toggle}
            className="ml-1 sm:ml-2 whitespace-nowrap font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-400 hover:text-zinc-50 border border-zinc-800 px-2 sm:px-3 py-2 transition-colors hover:border-zinc-600"
            data-testid="language-toggle"
            aria-label="Toggle language"
          >
            <span data-testid="lang-en" className={lang === "en" ? "text-zinc-50" : "text-zinc-500"}>EN</span>
            <span className="mx-2 text-zinc-700">/</span>
            <span data-testid="lang-es" className={lang === "es" ? "text-zinc-50" : "text-zinc-500"}>ES</span>
          </button>

          {user && user !== false ? (
            <>
              {!user.is_premium && (
                <Link
                  to="/upgrade"
                  aria-label={t("upgrade")}
                  className="hidden lg:inline-flex items-center gap-1.5 ml-2 px-3 py-2 whitespace-nowrap text-xs font-mono uppercase tracking-[0.25em] text-sienna-400 border border-sienna-400/40 hover:border-sienna-400 hover:bg-sienna-400/5 transition-colors"
                  data-testid="navbar-upgrade-link"
                >
                  <Sparkles size={12} />
                  <span className="hidden xl:inline">{t("upgrade")}</span>
                </Link>
              )}
              <NotificationBell />
              <Link
                to="/upload"
                className="ml-1 inline-flex items-center gap-2 whitespace-nowrap bg-sienna text-white px-3 lg:px-4 py-2 text-sm font-light hover:bg-sienna-400 transition-colors"
                data-testid="navbar-upload-button"
                aria-label={t("upload")}
              >
                <Upload size={14} />
                <span className="hidden sm:inline">{t("upload")}</span>
              </Link>
              <Link
                to={portfolioHref(user)}
                className="relative ml-1 p-2 text-zinc-400 hover:text-zinc-50 border border-transparent hover:border-zinc-800 transition-colors"
                data-testid="navbar-profile-link"
                aria-label={t("my_profile")}
              >
                <UserIcon size={16} />
                {user.is_premium && (
                  <span
                    className="absolute top-1 right-1 h-2 w-2 rounded-full bg-sienna"
                    data-testid="navbar-verified-indicator"
                  />
                )}
              </Link>
              <button
                onClick={onLogout}
                className="p-2 text-zinc-400 hover:text-zinc-50 border border-transparent hover:border-zinc-800 transition-colors"
                data-testid="navbar-logout-button"
                aria-label={t("sign_out")}
              >
                <LogOut size={16} />
              </button>
            </>
          ) : (
            <>
              <Link
                to="/login"
                className="hidden sm:inline-block ml-1 sm:ml-2 whitespace-nowrap px-3 sm:px-4 py-2 text-sm font-light text-zinc-300 hover:text-zinc-50 border border-transparent hover:border-zinc-800 transition-colors"
                data-testid="navbar-login-link"
              >
                {t("sign_in")}
              </Link>
              <Link
                to="/register"
                className="inline-flex items-center whitespace-nowrap bg-sienna text-white px-3 sm:px-4 py-2 text-sm font-light hover:bg-sienna-400 transition-colors"
                data-testid="navbar-register-link"
              >
                {t("sign_up")}
              </Link>
            </>
          )}
        </nav>
      </div>
    </header>
  );
}
